/**
 * Red de seguridad para errores no atrapados.
 *
 * Sin esto, cualquier excepcion dentro de una pantalla desmonta el arbol
 * entero de React y el usuario se queda con una pagina EN BLANCO: sin menu,
 * sin aviso y sin forma de volver. Pasaba al abrir un expediente inexistente
 * (/cases/<id>): la API respondia 404 correctamente y el frontend se caia.
 *
 * Va por dentro del router para que el boton de volver funcione, y usa
 * `key={location.pathname}` para rearmarse al cambiar de ruta: sin esa llave,
 * una pantalla rota deja el error pegado y las demas tampoco se dibujan.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // La consola del navegador es lo unico que tenemos hoy para diagnosticar
    // en produccion. Si algun dia entra Sentry u otro, este es el sitio.
    console.error('Pantalla caida:', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center gap-4 p-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-destructive/10">
          <AlertTriangle className="h-6 w-6 text-destructive" />
        </div>
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">Esta pantalla no se pudo mostrar</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            El resto de la plataforma sigue funcionando. Si vuelve a pasar, avísanos
            con lo que estabas haciendo.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.history.back()}>
            Volver
          </Button>
          <Button onClick={() => window.location.assign('/')}>Ir al inicio</Button>
        </div>
        <details className="mt-2 max-w-lg text-left">
          <summary className="cursor-pointer text-xs text-muted-foreground">
            Detalle técnico
          </summary>
          <pre className="mt-2 overflow-auto rounded-md bg-muted p-3 text-xs">
            {this.state.error.message}
          </pre>
        </details>
      </div>
    );
  }
}
